import {AsyncPipe} from "@angular/common";
import {A11yModule} from "@angular/cdk/a11y";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  ViewChild,
  inject,
  signal,
} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {timer} from "rxjs";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {ModalService} from "src/app/modules/core/services/modal.service";
import {ScreenSizeService} from "src/app/modules/shared/services/screenSize.service";
import {UserAvatarComponent} from "src/app/modules/shared/components/user-avatar/user-avatar.component";
import {environment} from "src/environments/environment";
import {DiscussionInboxItem} from "../../interfaces/discussion-inbox.interface";
import {RecommendationDiscussionsService} from "../../services/recommendation-discussions.service";
import {RecommendationDiscussionComponent} from "../recommendation-discussion/recommendation-discussion.component";
import {DiscussionSafetyDialogComponent} from "../discussion-safety-dialog/discussion-safety-dialog.component";
import {DiscussionPreviewComponent} from "../discussion-preview/discussion-preview.component";
import {DiscussionPanelHeaderComponent} from "../discussion-panel-header/discussion-panel-header.component";
import {DiscussionLoadingComponent} from "../discussion-loading/discussion-loading.component";

@Component({
  selector: "app-recommendation-discussion-host",
  templateUrl: "./recommendation-discussion-host.component.html",
  styleUrl: "./recommendation-discussion-host.component.css",
  imports: [
    AsyncPipe,
    A11yModule,
    TranslateModule,
    UserAvatarComponent,
    DiscussionPreviewComponent,
    DiscussionPanelHeaderComponent,
    DiscussionLoadingComponent,
    RecommendationDiscussionComponent,
    DiscussionSafetyDialogComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecommendationDiscussionHostComponent {
  readonly discussions = inject(RecommendationDiscussionsService);
  readonly screen = inject(ScreenSizeService);
  private readonly auth = inject(AuthService);
  private readonly modals = inject(ModalService);
  private readonly translate = inject(TranslateService);
  readonly imagesDomain = environment.imagesDomain;
  readonly now = signal(Date.now());
  readonly scrolled = signal(false);
  list?: ElementRef<HTMLElement>;
  @ViewChild("list") set listView(ref: ElementRef<HTMLElement> | undefined) {
    this.list = ref;
    queueMicrotask(() => {
      if (ref && this.list === ref && ref.nativeElement.isConnected)
        ref.nativeElement.scrollTop = this.discussions.listScrollTop;
    });
  }

  constructor() {
    timer(60000, 60000)
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(() => this.now.set(Date.now()));
  }

  @HostListener("document:keydown.escape", ["$event"])
  escape(event: Event) {
    if (!this.discussions.opened() || event.defaultPrevented) return;
    const safetyId = `discussion-safety-${this.discussions.selectedId()}`;
    if (this.modals.isModalOpen(safetyId)) {
      event.preventDefault();
      this.modals.closeModal(safetyId);
      return;
    }
    this.discussions.close();
  }

  friend(item: DiscussionInboxItem) {
    return item.sender._id === this.auth.user?._id
      ? item.receiver
      : item.sender;
  }

  own(id: string) {
    return id === this.auth.user?._id;
  }

  relative(value: string): string {
    const minutes = Math.min(
      0,
      Math.round((new Date(value).getTime() - this.now()) / 60000)
    );
    const format = new Intl.RelativeTimeFormat(
      this.translate.currentLang || "en",
      {numeric: "auto"}
    );
    if (minutes > -60) return format.format(minutes, "minute");
    if (minutes > -1440) return format.format(Math.round(minutes / 60), "hour");
    return format.format(Math.round(minutes / 1440), "day");
  }

  scrollList(element: HTMLElement) {
    this.discussions.listScrollTop = element.scrollTop;
    this.scrolled.set(element.scrollTop > 0);
    if (element.scrollTop <= 8) this.discussions.applyActivity();
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 160)
      this.discussions.loadMoreInbox();
  }

  activity() {
    this.discussions.applyActivity();
    this.list?.nativeElement.scrollTo({top: 0});
  }
}
